import { jwtDecode } from "jwt-decode";

/**
 * Render-gate for the WhatsApp inbox and settings.
 *
 * Patient conversations are clinical-adjacent, so they are not visible to every
 * role by default. Mirrors Tasks/access.js — JWT-based, so a stale token after a
 * server-side feature change still gates correctly until the next login.
 *
 * UX-only: whatsappRoutes.js enforces the same rules (requireWhatsAppFeature)
 * at the API layer regardless of what the client renders.
 */

const INBOX_ROLES = [
  "SuperAdmin",
  "SystemAdmin",
  "Admin",
  "Reception",
  "Practitioner",
];

function decode() {
  try {
    const token = sessionStorage.getItem("user");
    if (!token) return null;
    return jwtDecode(token);
  } catch (_e) {
    return null;
  }
}

// Role membership is spread across three claim shapes in this codebase.
function rolesOf(decoded) {
  const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  return new Set([
    ...asArray(decoded?.role),
    ...asArray(decoded?.roles),
    ...asArray(decoded?.permissions),
  ]);
}

/**
 * Is WhatsApp available to this user at all?
 *
 * `features.whatsapp` is resolved server-side from the practice TIER and the
 * user's own flag (models/practiceTiers.js resolveFeatures), so this is the
 * tier check as far as the client can see it — a UI hint that can be up to 12
 * hours stale, which is why every route re-checks the target practice.
 *
 * Separate from userCanUseWhatsApp because recording a patient's consent is not
 * the same act as reading their conversations: consent belongs on the patient
 * record, which more roles can edit than can open the inbox.
 */
export function whatsAppFeatureEnabled() {
  return decode()?.features?.whatsapp === true;
}

export function userCanUseWhatsApp() {
  const decoded = decode();
  if (decoded?.features?.whatsapp !== true) return false;
  const roles = rolesOf(decoded);
  return INBOX_ROLES.some((r) => roles.has(r));
}

export function userCanConfigureWhatsApp() {
  const decoded = decode();
  if (!decoded) return false;
  const roles = rolesOf(decoded);
  return roles.has("SuperAdmin") || roles.has("SystemAdmin");
}

export function userPractices() {
  const decoded = decode();
  const practices = decoded?.practice;
  if (Array.isArray(practices)) return practices.map(String);
  return practices ? [String(practices)] : [];
}
